import { Box } from '@mui/material'
import { Form } from 'react-final-form'
import { DirtyFormSpy } from 'src/shared/form/dirty/DirtyFormSpy'
import { useCallback, useContext, useEffect, useRef, useState } from 'react'
import { CONCLUSION_RATING, MILESTONE_STATUS, MILESTONE_TYPE } from 'src/shared/constants/milestone-constants'
import { useParams } from 'react-router'
import { useNotificationService } from 'src/shared/utils/NotificationService'
import { useDelayedNavigate } from 'src/shared/utils/hooks/navigation-hooks'
import { useClient } from 'urql'
import { Utils } from 'src/shared/utils/Utils'
import { useMessageSource } from 'src/i18n/useMessageSource'
import { Mutation_Root, Query_Root } from 'src/@types/graphql'
import {
  milestoneConclusionDataQuery,
  updateMilestoneConclusionRatingQuery,
} from 'src/screens/shared/implementation/details/milestoneQueries'
import { SaveAndBackButton, SaveButton } from 'src/shared/button/Buttons'
import { HelpAndInstructions } from 'src/shared/presentation/HelpAndInstructions'
import { PageLayout } from 'src/shared/layout/PageLayout'
import { ScreenLayout } from 'src/shared/layout/ScreenLayout'
import { ROUTES } from 'src/routing/routes'
import { AutoCompleteField } from 'src/shared/form/control/AutoCompleteField'
import { UserContext } from 'src/user/UserContext'
import { NotAuthorized } from 'src/shared/not-authorized/NotAuthorized'
import { required } from 'src/shared/form/validation/validators'

interface Props {
  baseUrl: '/pf-kap' | '/pf-pgv'
}

type originType = 'SAVE' | 'SAVE_AND_BACK'

interface FormValues {
  rating: string | null
}

export const FinalReportConclusionRatingEditPage = ({ baseUrl }: Props) => {
  const { projectId, milestoneId } = useParams()

  const notificationService = useNotificationService()
  const navigate = useDelayedNavigate()
  const urqlClient = useClient()
  const process = Utils.resolveProcess(baseUrl)
  const milestonePath = Utils.resolveMilestonePath(MILESTONE_TYPE.FINAL_REPORT)

  const originRef = useRef<originType>('SAVE')

  const { user } = useContext(UserContext)
  const { getMessage } = useMessageSource()

  const [initialValues, setInitialValues] = useState<FormValues | undefined>()
  const [canEdit, setCanEdit] = useState<boolean>()

  const ratingOptions = Object.keys(CONCLUSION_RATING).map((key) => ({
    label: `${getMessage(`label.milestone.conclusion.${key}`)}`,
    value: key,
  }))

  const fetchData = useCallback(async () => {
    const { data } = await urqlClient
      .query<{ milestone_by_pk: Query_Root['milestone_by_pk'] }>(milestoneConclusionDataQuery, { milestoneId })
      .toPromise()

    if (data?.milestone_by_pk) {
      setInitialValues({
        rating: data.milestone_by_pk.rating ?? null,
      })
      setCanEdit(data.milestone_by_pk.status !== MILESTONE_STATUS.DONE && Utils.isInternalRole(user.roles))
    }
  }, [milestoneId, urqlClient, projectId])

  useEffect(() => {
    fetchData()
  }, [fetchData])

  const onBack = () => {
    navigate(ROUTES.PfKapFinalReportMilestoneRoot.nested.Conclusion.params({ projectId, milestoneId, milestonePath }))
  }

  const onSubmit = (origin: originType) => (_: any) => {
    // trigger submit event
    if (originRef) {
      originRef.current = origin
    }
    document.getElementById('edit-form')?.dispatchEvent(new Event('submit', { cancelable: true, bubbles: true }))
  }

  const updateHandler = async (values: FormValues) => {
    const { data, error } = await urqlClient
      .mutation<{ update_milestone: Mutation_Root['update_milestone'] }>(updateMilestoneConclusionRatingQuery, {
        id: milestoneId,
        rating: values.rating,
      })
      .toPromise()

    if (error || data?.update_milestone?.affected_rows !== 1) {
      notificationService.operationFailed()
    } else {
      setInitialValues({ rating: values.rating })
      notificationService.changesSaved()
      if (originRef.current === 'SAVE_AND_BACK') {
        onBack()
      }
    }
  }

  if (canEdit === false) {
    return <NotAuthorized inSecondLevelNavigationTab />
  }

  return (
    <ScreenLayout
      title={getMessage(`label.milestone.conclusion.rating.edit`)}
      hasSecondLevelNavigation={false}
      onBack={onBack}
      actions={
        <>
          <SaveAndBackButton origin="header" onClick={onSubmit('SAVE_AND_BACK')} />
          <SaveButton origin="header" onClick={onSubmit('SAVE')} />
        </>
      }
    >
      <PageLayout>
        <HelpAndInstructions labelKey={`label.help.milestone.conclusion.rating.${process.toLowerCase()}`} />

        <Box>
          {initialValues && (
            <Form<FormValues>
              initialValues={initialValues}
              onSubmit={updateHandler}
              render={({ handleSubmit }) => {
                return (
                  <form id="edit-form" onSubmit={handleSubmit}>
                    <AutoCompleteField
                      name="rating"
                      label={getMessage('label.milestone.conclusion.rating')}
                      options={ratingOptions}
                      validate={required()}
                      required
                    />

                    <DirtyFormSpy />
                  </form>
                )
              }}
            />
          )}
        </Box>
      </PageLayout>
    </ScreenLayout>
  )
}
